import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import {
  getApiV1IndividualpaymentFinancialPeopleFinancialpersonidEditorQueryKey,
  useDeleteApiV1IndividualpaymentFinancialPeopleFinancialpersonidCouponsCouponid,
  usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidCoupons,
} from '@/shared/api/generated';

/**
 * Aplicação e remoção de cupom de desconto.
 *
 * Assimetria deliberada com a tabela de descontos: `discounts[]` é estado do
 * formulário e só persiste no Salvar, enquanto cupom **persiste na hora** — tem
 * endpoint próprio e invalida a query do editor, que é quem devolve os valores
 * recalculados. A UI precisa deixar essa diferença visível.
 *
 * Os dois endpoints recebem tudo em **query params**, não em corpo: `couponId` e
 * `description` no POST, `justification` no DELETE.
 *
 * @param financialPersonId - ID do contrato financeiro; `null` desabilita as ações.
 *
 * @example
 * ```tsx
 * const { applyCoupon, removeCoupon, isPending } = usePlanCoupons(101);
 * applyCoupon({ couponId: 7, description: 'Campanha de retenção' });
 * ```
 */
export function usePlanCoupons(financialPersonId: number | null) {
  const queryClient = useQueryClient();
  const applyMutation = usePostApiV1IndividualpaymentFinancialPeopleFinancialpersonidCoupons();
  const removeMutation =
    useDeleteApiV1IndividualpaymentFinancialPeopleFinancialpersonidCouponsCouponid();

  const invalidateEditor = useCallback(() => {
    if (financialPersonId === null) return;
    void queryClient.invalidateQueries({
      queryKey:
        getApiV1IndividualpaymentFinancialPeopleFinancialpersonidEditorQueryKey(financialPersonId),
    });
  }, [financialPersonId, queryClient]);

  const applyCoupon = useCallback(
    (
      input: { readonly couponId: number; readonly description: string },
      options?: { readonly onSuccess?: () => void },
    ) => {
      if (financialPersonId === null) return;
      applyMutation.mutate(
        { financialPersonId, params: { couponId: input.couponId, description: input.description } },
        {
          onSuccess: () => {
            invalidateEditor();
            options?.onSuccess?.();
          },
        },
      );
    },
    [applyMutation, financialPersonId, invalidateEditor],
  );

  const removeCoupon = useCallback(
    (
      input: { readonly couponId: number; readonly justification: string },
      options?: { readonly onSuccess?: () => void },
    ) => {
      if (financialPersonId === null) return;
      removeMutation.mutate(
        {
          financialPersonId,
          couponId: input.couponId,
          params: { justification: input.justification },
        },
        {
          onSuccess: () => {
            invalidateEditor();
            options?.onSuccess?.();
          },
        },
      );
    },
    [financialPersonId, invalidateEditor, removeMutation],
  );

  return {
    applyCoupon,
    removeCoupon,
    isPending: applyMutation.isPending || removeMutation.isPending,
    isError: applyMutation.isError || removeMutation.isError,
  } as const;
}
